// Pending writes: what the user changed that GitHub (or the changelist
// server) has not confirmed yet.
//
// The contract, for every store and view that writes:
//
// - A write is a value (`WriteSpec`): what it is (`kind`, `label`, `done`),
//   what it is the same write as (`dedup`, required), which items it shows
//   on (`subject`), the change it makes (`apply`, pure), the unsent text
//   it sends (`draft`), how to send it (`send`) and how the server's
//   answer reaches the store (`confirm`). Being a value is what lets Retry
//   run it again exactly as it was.
// - The store keeps only what the server last sent, and shows
//   `overlay(server)`: that state with every pending write's `apply` laid
//   over it, oldest first. A reload that lands while a write is pending is
//   overlaid again, so it cannot wipe the write out; a failed write rolls
//   back by being removed, with nothing to undo by hand.
// - Life of a write:
//
//     (queued ──the write before it is done──▶) sending ──ok──▶ reloading ──fresh copy lands (settle)──▶ gone
//        │               └──reload fails (stall)──▶ stalled ──settle──▶ gone
//        │   (a `land` write puts the server's answer in and is gone in one step)
//        └──fails──▶ gone, listed in `failures` until retried, retired or given up
//
//   `pending`, `on` and `active` (the progress line) count the writes in
//   flight: queued, sending or reloading. A stalled write is still shown
//   (the server accepted it) but no longer marked pending.
// - Settling is ticketed: a reload confirm records the ticket of the
//   resource's newest fetch when the write was accepted, and
//   `settle(resource, ticket)` drops only writes accepted before the fetch
//   with that ticket began. The store calls it in the same step that
//   stores the fetched copy, so nothing is ever shown twice or missing.
// - Nothing is sent twice. Writes with the same `dedup` are the same write:
//   starting one retires every failure with that key (whose Retry then
//   does nothing), and starting one while another with that key is being
//   sent is refused. Retry is one-shot. A failure is also retired when its
//   toast is given up (`dismiss`).
// - Only the latest intent counts, for a `latest` write (an idempotent one
//   that sets a state, e.g. one login's attention). Only the order in which
//   the server receives them matters, so per `dedup` at most one is being
//   sent, until the server accepted it or it failed, and one more waits,
//   shown and pending (`queued`) but not sent. A newer one replaces the one
//   waiting, which is never sent: its caller is answered by the write that
//   replaced it. As soon as the one being sent is accepted or has failed,
//   the waiting one is sent; reloads never hold it back. A write that fails
//   while a newer one waits is not listed in `failures` (its Retry would
//   undo the newer intent).
// - No text is lost. A write with `draft` takes the text out of its box
//   into the box's outbox (drafts.ts): the box is free for new text, and
//   `holds(key)` tells it not to offer the taken text again while the
//   write is sent or its Retry is live. Success deletes only the taken
//   copy. A failure keeps its own copy (`text`); when it is given up, or
//   retired by a different text sent from the same box, that copy goes
//   back to the box's saved draft, merged with what was typed meanwhile,
//   and to the box itself if one is open (`listen`). It is an event, not
//   state: a box opened later finds the text in its saved draft only.
//
// - A box tells its own save. While it is open (`tell`), a box reads how
//   its text stands from `box(key)`: idle, saving (from the step its write
//   starts in) or failed, with why. It stays open through the save, so its
//   button shows the press was taken, and closes when the write answers
//   true. Its failure is told in it and not by the toast (`untold`), and
//   since the failed text is still on screen in it, what it sends next
//   replaces that text: none of it comes back. When the box closes with
//   its failure live, the toast tells it, as for a box that was never open.
//
// Components render from this state (`on`, `pending`, `active`,
// `failures`, `box`) and keep no in-flight flags of their own.
import { untrack } from 'svelte';
import type { WriteError } from '../api/errors';
import type { PrWriteKind } from '../runtime/appLog';
import { returnDraft, sentDraft, takeDraft } from './drafts';

/** What a write does; components and the drafts inbox tell writes apart by it. */
export type WriteKind =
  | 'thread'
  | 'reply'
  | 'quick-reply'
  | 'resolve'
  | 'unresolve'
  | 'delete-draft'
  | 'held-add'
  | 'held-edit'
  | 'held-delete'
  | 'draft-state'
  | 'mirror'
  | 'comment'
  | 'attention'
  | 'merge'
  | 'publish'
  | 'post-held'
  | 'discard'
  | 'request-review';

/** The kind of change a write makes to its pull request, as the invalidation log records it (runtime/cache.ts). */
export function logKind(kind: WriteKind): PrWriteKind {
  switch (kind) {
    case 'resolve':
    case 'unresolve':
      return 'resolve';
    case 'thread':
    case 'reply':
    case 'quick-reply':
    case 'comment':
    case 'mirror':
      return 'reply';
    case 'request-review':
      return 'rerequest';
    case 'publish':
    case 'post-held':
    case 'discard':
    case 'merge':
      return 'publish';
    case 'delete-draft':
    case 'held-add':
    case 'held-edit':
    case 'held-delete':
    case 'draft-state':
    case 'attention':
      return 'review';
  }
}

/** The write adds its box's text as a new item (a draft comment, a reply), where an edit changes one that stays. */
export const addsItem = (kind: WriteKind): boolean => kind === 'thread' || kind === 'held-add' || kind === 'reply';

/**
 * A write's sender gave it up before it was answered (the signed-in
 * account changed): it may have gone through, and it is nobody's to send
 * again. It leaves the pending writes without a failure, a Retry, a
 * reload, or a word to its caller's `onError`; its text stays in its
 * box's outbox.
 */
export class WriteAbandoned extends Error {
  constructor() {
    super('The signed-in account changed before this was saved; reload this page.');
    this.name = 'WriteAbandoned';
  }
}

/** What makes two writes the same write (see the file comment). Made only by `dedupKey`, through a store's named constructors. */
export type Dedup = string & { readonly __dedup: 'Dedup' };

/** A dedup key from its parts; stores wrap it in named constructors (e.g. `dedup.quick(threadId)`). */
export const dedupKey = (...parts: (string | number)[]): Dedup => parts.join(':') as Dedup;

/**
 * How the server's answer reaches the state the store keeps:
 * - `land`: the answer is the new state; it is put in and the write
 *   dropped in one step.
 * - `reload`: the answer comes with a fresh copy of `reload`. `ticket`
 *   gives the ticket of that resource's newest fetch so far; `run` fetches
 *   a new copy (the store settles or stalls the resource when it lands).
 *   `run` also runs after a failure, since the server may have applied part
 *   of the write.
 */
export type Confirm<R extends string, T> =
  | { land: (answer: T) => void }
  | { reload: R; ticket: () => number; run: () => Promise<void> };

/** A write to run. A value, so Retry runs it again as it was. */
export interface WriteSpec<S, R extends string, K extends string, T = void> {
  readonly kind: WriteKind;
  /** What it is the same write as: required, so no write can be sent twice by accident. */
  readonly dedup: Dedup;
  /** What is being done ("Saving reply"): shown as "Saving reply…" while pending, "Saving reply failed: …" when it fails. */
  readonly label: string;
  /**
   * Announced when the server accepted it ("Reply saved as a draft"). Given
   * the answer, it may say nothing (null): the write had nothing to do.
   */
  readonly done: string | ((answer: T) => string | null);
  /** The items it shows on, looked up with `on`. */
  readonly subject?: K | readonly K[];
  /** The change, applied to the state without it. Pure: it runs again over every state the server sends while the write is shown. */
  readonly apply?: (s: S) => S;
  /** The unsent text it sends, and the box (`key`, see drafts.ts) it came from. Never on a `latest` write. */
  readonly draft?: { readonly key: string; readonly text: string };
  /**
   * It sets a state, so only the latest one per `dedup` matters: sent once
   * the server has answered the one being sent, replacing any already
   * waiting, instead of being refused (see the file comment).
   */
  readonly latest?: true;
  /** Send it; resolves with the server's answer once accepted. */
  readonly send: () => Promise<T>;
  readonly confirm: Confirm<R, T>;
  /** Told why it failed, besides `failures` (e.g. the Review view's own message). */
  readonly onError?: (error: WriteError) => void;
  /** Its caller tells a failure and offers its own Retry (the Review view's hand-off): it is not listed in `failures`. */
  readonly unlisted?: true;
}

/** A write in flight, as components see it. */
export interface PendingWrite<K extends string> {
  readonly id: number;
  readonly kind: WriteKind;
  readonly dedup: Dedup;
  readonly label: string;
  readonly subjects: readonly K[];
  /** The box whose text it sends, or null. */
  readonly draft: string | null;
}

/** A write that failed and was rolled back, while its Retry is live. */
export interface FailedWrite<K extends string> extends PendingWrite<K> {
  readonly error: WriteError;
  /** Its own copy of the unsent text, or null. */
  readonly text: string | null;
  /** Run the same write again, once; false (and nothing sent) when this failure is no longer listed. */
  retry(): Promise<boolean>;
  /** Give up on it: its text goes back to its box. */
  dismiss(): void;
}

/**
 * How a box's text stands with the server: nothing of it being sent,
 * being sent, or not saved, with why and the text the write holds (see
 * `Writes.box`). `label` is what the write does ("Saving draft").
 */
export type Save =
  | { readonly at: 'idle' }
  | { readonly at: 'saving'; readonly label: string }
  | { readonly at: 'failed'; readonly label: string; readonly error: WriteError; readonly text: string | null };

export const IDLE: Save = { at: 'idle' };

/** Several saves a box shows as one (the parts of what it sends): saving while any is, else the first that failed, else idle. */
export function saveOf(...saves: readonly Save[]): Save {
  return saves.find((s) => s.at === 'saving') ?? saves.find((s) => s.at === 'failed') ?? IDLE;
}

/**
 * The boxes one component tells (see `Writes.tell`): `tell` each key from
 * the moment the component shows that box's text and save itself, `release`
 * them all when it stops (its box closes, or the component goes).
 */
export function tellers(writes: { tell(key: string): () => void }): { tell(key: string): void; release(): void } {
  const told = new Map<string, () => void>();
  return {
    tell: (key) => {
      if (!told.has(key)) told.set(key, writes.tell(key));
    },
    release: () => {
      for (const stop of told.values()) stop();
      told.clear();
    },
  };
}

/** Something to announce: a new value each time, so the same words are announced again. */
export interface News {
  readonly id: number;
  readonly text: string;
}

/** How a send went: refused or failed, or accepted, with `reloaded` resolving (never rejecting) once its reload is done. */
type Sent = { ok: false } | { ok: true; reloaded: Promise<void> };

type Phase<R> =
  | { at: 'queued' }
  | { at: 'sending' }
  | { at: 'reloading'; resource: R; after: number }
  | { at: 'stalled'; resource: R; after: number };

interface Entry<S, R, K extends string> {
  write: PendingWrite<K>;
  apply: ((s: S) => S) | null;
  phase: Phase<R>;
}

/** The `latest` writes with one `dedup`: one being sent, and the one waiting to be sent once the server answers it. */
interface Lane<S, R extends string, K extends string> {
  next: { spec: WriteSpec<S, R, K, unknown>; entry: Entry<S, R, K>; answer: ((ok: boolean) => void)[] } | null;
}

/** A write's `subject`, as a list. */
function subjectList<K extends string>(subject: K | readonly K[] | undefined): readonly K[] {
  return subject === undefined ? [] : typeof subject === 'string' ? [subject] : [...(subject as readonly K[])];
}

export class Writes<S, R extends string, K extends string> {
  #entries: Entry<S, R, K>[] = $state.raw([]);
  private get entries(): Entry<S, R, K>[] {
    return this.#entries;
  }
  private set entries(next: Entry<S, R, K>[]) {
    this.#entries = next;
    this.changed();
  }
  private seq = 0;
  private lanes = new Map<Dedup, Lane<S, R, K>>();
  /** Each `latest` lane's drive, done once the lane is free (see `laneFree`). */
  private driving = new Map<Dedup, Promise<void>>();

  #failures: readonly FailedWrite<K>[] = $state.raw([]);
  /** Failed writes whose Retry is live, oldest first. */
  get failures(): readonly FailedWrite<K>[] {
    return this.#failures;
  }
  private setFailures(next: readonly FailedWrite<K>[]) {
    this.#failures = next;
    this.changed();
  }

  /** Told each time the writes become `idle` (see `whenIdle`). */
  private idlers = new Set<() => void>();

  /** The last write the server accepted, or one refused as a duplicate, worded for a polite live region (failures are told through `failures`). */
  news: News | null = $state.raw(null);

  /** Open boxes, by key, told when text comes back to them (see `listen`). */
  private listeners = new Map<string, Set<(text: string) => void>>();

  /** How many open boxes tell each key's save themselves (see `tell`). */
  #telling: ReadonlyMap<string, number> = $state.raw(new Map());

  /** `describe` words a send's error for the user. */
  /**
   * `perform` sends a write, given its kind and its send, and settles with
   * the answer: a store whose sends are effects of its runtime (the pull
   * request page's Program, pr/outbox.ts) passes how; by default the send
   * is simply called.
   */
  constructor(
    private describe: (e: unknown) => WriteError,
    private perform: <T>(kind: WriteKind, send: () => Promise<T>) => Promise<T> = (_kind, send) => send(),
  ) {}

  /**
   * While box `key` is open: `take` is called with text given back to it
   * (merge it into what the box shows). Returns the unsubscribe; call it
   * when the box closes. Text given back while no box listens is only in
   * the saved draft.
   */
  listen(key: string, take: (text: string) => void): () => void {
    let set = this.listeners.get(key);
    if (!set) this.listeners.set(key, (set = new Set()));
    set.add(take);
    return () => {
      set.delete(take);
      if (!set.size && this.listeners.get(key) === set) this.listeners.delete(key);
    };
  }

  /**
   * Box `key` is open and shows its text and `box(key)` itself, from now
   * until the returned release is called (once counts; call it when the
   * box closes): its failure is not the toast's (`untold`), what it sends
   * next replaces the failed text, and the new item its write adds is not
   * shown beside it (`shownInBox`).
   */
  tell(key: string): () => void {
    const count = (by: number) => {
      const now = untrack(() => this.#telling);
      const n = (now.get(key) ?? 0) + by;
      const next = new Map(now);
      if (n > 0) next.set(key, n);
      else next.delete(key);
      this.#telling = next;
    };
    count(1);
    let open = true;
    return () => {
      if (!open) return;
      open = false;
      count(-1);
    };
  }

  /** An open box shows `key`'s text and save itself (see `tell`). */
  tells(key: string): boolean {
    return this.#telling.has(key);
  }

  /**
   * The item `subject` is new text still on screen in the open box that is
   * sending it: it is shown there, and as an item once the box closes or
   * the server has it.
   */
  shownInBox(subject: K): boolean {
    const w = this.on(subject);
    return !!w && w.draft !== null && addsItem(w.kind) && this.tells(w.draft);
  }

  /** How box `key`'s text stands: being sent by a write, else not saved by the newest write that failed with it, else idle. */
  box(key: string): Save {
    const sending = this.entries.find((e) => e.phase.at === 'sending' && e.write.draft === key);
    if (sending) return { at: 'saving', label: sending.write.label };
    const failed = this.failures.filter((f) => f.draft === key).at(-1);
    return failed ? { at: 'failed', label: failed.label, error: failed.error, text: failed.text } : IDLE;
  }

  /** The failures no open box tells: the toast's. */
  get untold(): readonly FailedWrite<K>[] {
    return this.failures.filter((f) => f.draft === null || !this.tells(f.draft));
  }

  /** Writes in flight (being sent, or reloaded after they were accepted), oldest first. A stalled write is shown but not listed. */
  get pending(): readonly PendingWrite<K>[] {
    return this.entries.filter((e) => e.phase.at !== 'stalled').map((e) => e.write);
  }

  /** Any write is in flight (the progress line). */
  get active(): boolean {
    return this.entries.some((e) => e.phase.at !== 'stalled');
  }

  /**
   * Call `then` each time nothing is in flight any more (`active` turns
   * false), until the returned unsubscribe is called. Plain code, not an
   * effect: a store that no view holds any more waits on it.
   */
  whenIdle(then: () => void): () => void {
    this.wasIdle = !this.active;
    this.idlers.add(then);
    return () => void this.idlers.delete(then);
  }

  private wasIdle = true;
  private checking = false;
  /** Tell `whenIdle` once the current step is over (a Retry retires its failure, then sends: never idle between). */
  private changed() {
    if (this.checking || !this.idlers.size) return;
    this.checking = true;
    queueMicrotask(() => {
      this.checking = false;
      const idle = !this.active;
      if (idle === this.wasIdle) return;
      this.wasIdle = idle;
      if (idle) for (const then of [...this.idlers]) then();
    });
  }

  /** The newest write in flight on `subject`, or null. */
  on(subject: K): PendingWrite<K> | null {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const e = this.entries[i];
      if (e.phase.at !== 'stalled' && e.write.subjects.includes(subject)) return e.write;
    }
    return null;
  }

  /**
   * Resolves once no `latest` write with `dedup` is being sent or waits to
   * be (at once if none is): the next one made goes out at once. A reload
   * of the last one may still run. For tests, which must know the lane
   * moved on.
   */
  laneFree(dedup: Dedup): Promise<void> {
    return this.driving.get(dedup) ?? Promise.resolve();
  }

  /** A write with `dedup` is being sent: another is refused until it is done. */
  sending(dedup: Dedup): boolean {
    return this.entries.some((e) => e.phase.at === 'sending' && e.write.dedup === dedup);
  }

  /** A write is sending box `key`'s taken text, or a Retry would send it: the box must not offer it again. */
  holds(key: string): boolean {
    return this.entries.some((e) => e.phase.at === 'sending' && e.write.draft === key) || this.failures.some((f) => f.draft === key);
  }

  /** `server` with every pending write applied, oldest first. */
  overlay(server: S): S {
    let s = server;
    for (const e of this.entries) if (e.apply) s = e.apply(s);
    return s;
  }

  /**
   * A fresh copy of `resource`, fetched with `ticket`, was just stored: drop
   * the writes accepted before that fetch began. Call it in the same step
   * that stores the copy.
   */
  settle(resource: R, ticket: number): void {
    const confirmed = (p: Phase<R>) => (p.at === 'reloading' || p.at === 'stalled') && p.resource === resource && p.after < ticket;
    if (this.entries.some((e) => confirmed(e.phase))) this.entries = this.entries.filter((e) => !confirmed(e.phase));
  }

  /**
   * Fetching `resource` with `ticket` failed: the writes accepted before it
   * began stay shown (the server has them) but are no longer in flight,
   * until a later fetch settles them.
   */
  stall(resource: R, ticket: number): void {
    const hit = (p: Phase<R>) => p.at === 'reloading' && p.resource === resource && p.after < ticket;
    if (this.entries.some((e) => hit(e.phase)))
      this.entries = this.entries.map((e) => (e.phase.at === 'reloading' && hit(e.phase) ? { ...e, phase: { ...e.phase, at: 'stalled' } } : e));
  }

  /**
   * Show the write, send it, and reconcile (see `Confirm`). It retires the
   * failures with its `dedup` first; it is refused (false, nothing sent,
   * its text given back) while another with that `dedup` is being sent,
   * unless it is `latest` (then it waits; see the file comment). Resolves
   * true once the server accepted it (or the write that replaced it while
   * it waited) and its reload is done, false otherwise.
   */
  run<T>(spec: WriteSpec<S, R, K, T>): Promise<boolean> {
    return this.start(spec, false);
  }

  private start<T>(spec: WriteSpec<S, R, K, T>, retry: boolean): Promise<boolean> {
    if (!spec.latest) return this.send(spec, retry, () => false).then((sent) => sent.ok && sent.reloaded.then(() => true));
    // A waiting write may be replaced unsent, so it must hold no text.
    if (spec.draft) throw new TypeError('a latest write carries no draft');
    const lane = this.lanes.get(spec.dedup);
    if (!lane)
      return new Promise((answer) => {
        const driven = this.drive(spec as WriteSpec<S, R, K, unknown>, [answer]);
        this.driving.set(spec.dedup, driven);
        void driven.then(() => this.driving.get(spec.dedup) === driven && this.driving.delete(spec.dedup));
      });
    return new Promise((answer) => {
      // The newest intent: older failures and the one waiting are superseded.
      this.retire((f) => f.dedup === spec.dedup);
      const write = this.pendingWrite(spec);
      const entry: Entry<S, R, K> = { write, apply: spec.apply ?? null, phase: { at: 'queued' } };
      const before = lane.next;
      lane.next = { spec: spec as WriteSpec<S, R, K, unknown>, entry, answer: [...(before?.answer ?? []), answer] };
      this.entries = [...this.entries.filter((e) => e !== before?.entry), entry];
    });
  }

  /**
   * Send `spec` and then each write that waits behind it, each as soon as
   * the server has answered the one before, until none waits. Callers are
   * answered when their write's reload is done (false at once if it
   * failed). If anything throws, the lane is freed, the one waiting is
   * dropped, and every caller not yet answered gets false.
   */
  private async drive(spec: WriteSpec<S, R, K, unknown>, answer: ((ok: boolean) => void)[]) {
    const dedup = spec.dedup;
    const lane: Lane<S, R, K> = { next: null };
    this.lanes.set(dedup, lane);
    try {
      for (;;) {
        const sent = await this.send(spec, false, () => lane.next !== null);
        const callers = answer;
        answer = [];
        if (sent.ok) void sent.reloaded.then(() => callers.forEach((a) => a(true)));
        else callers.forEach((a) => a(false));
        const next = lane.next;
        if (!next) break;
        lane.next = null;
        this.entries = this.entries.filter((e) => e !== next.entry);
        ({ spec, answer } = next);
      }
    } catch (e) {
      console.error('a write could not be finished', e);
    } finally {
      this.lanes.delete(dedup);
      const waiting = lane.next;
      lane.next = null;
      if (waiting) this.entries = this.entries.filter((e) => e !== waiting.entry);
      for (const a of [...answer, ...(waiting?.answer ?? [])]) a(false);
    }
  }

  private pendingWrite<T>(spec: WriteSpec<S, R, K, T>): PendingWrite<K> {
    return { id: ++this.seq, kind: spec.kind, dedup: spec.dedup, label: spec.label, subjects: subjectList(spec.subject), draft: spec.draft?.key ?? null };
  }

  /**
   * `retry`: the failure's own text is already out of its box (in the
   * outbox), and the box may hold new text. `superseded`: a newer write
   * waits, so a failure is not listed. Resolves once the server answered;
   * an accepted write's reload goes on in `reloaded`. If anything throws,
   * the write is taken off the list before the throw goes on.
   */
  private async send<T>(spec: WriteSpec<S, R, K, T>, retry: boolean, superseded: () => boolean): Promise<Sent> {
    const draft = spec.draft ?? null;
    if (this.sending(spec.dedup)) {
      if (draft) this.giveBack(draft.key, draft.text);
      this.news = { id: (this.news?.id ?? 0) + 1, text: `Already sending: ${spec.label.toLowerCase()}. Wait for it to finish.` };
      return { ok: false };
    }
    // A resend: the stale Retry goes; its text comes back unless this sends it, or its box still shows it (`tell`).
    const shown = !!draft && untrack(() => this.#telling).has(draft.key);
    this.retire(
      (f) => f.dedup === spec.dedup,
      (f) => !!draft && f.draft === draft.key && (shown || draft.text.includes((f.text ?? '').trim())),
    );
    const write = this.pendingWrite(spec);
    if (draft && !retry) void takeDraft(draft.key, draft.text);
    this.entries = [...this.entries, { write, apply: spec.apply ?? null, phase: { at: 'sending' } }];
    const drop = () => (this.entries = this.entries.filter((x) => x.write !== write));
    let answer: T;
    try {
      answer = await this.perform(spec.kind, spec.send);
    } catch (e) {
      drop();
      if (e instanceof WriteAbandoned) return { ok: false };
      const error = this.describe(e);
      spec.onError?.(error);
      if (!superseded() && !spec.unlisted) this.fail(write, error, draft?.text ?? null, () => this.start(spec, true));
      if ('reload' in spec.confirm) void this.reload(spec.confirm.run, null);
      return { ok: false };
    }
    try {
      if (draft) sentDraft(draft.key);
      const said = typeof spec.done === 'function' ? spec.done(answer) : spec.done;
      if (said !== null) this.news = { id: (this.news?.id ?? 0) + 1, text: said };
      if ('land' in spec.confirm) {
        spec.confirm.land(answer);
        drop();
        return { ok: true, reloaded: Promise.resolve() };
      }
      const { reload, ticket, run } = spec.confirm;
      const phase: Phase<R> = { at: 'reloading', resource: reload, after: ticket() };
      this.entries = this.entries.map((x) => (x.write === write ? { ...x, phase } : x));
      return { ok: true, reloaded: this.reload(run, write) };
    } catch (e) {
      drop();
      throw e;
    }
  }

  /**
   * Run a confirm's reload. It never rejects: a reload that throws (the
   * store's `run` should stall the resource itself) stalls `write`, which
   * stays shown since the server accepted it.
   */
  private reload(run: () => Promise<void>, write: PendingWrite<K> | null): Promise<void> {
    return new Promise<void>((done) => done(run())).catch((e) => {
      console.error('a reload after a write failed', e);
      if (write)
        this.entries = this.entries.map((x) => (x.write === write && x.phase.at === 'reloading' ? { ...x, phase: { ...x.phase, at: 'stalled' } } : x));
    });
  }

  /**
   * List a failure that `run` did not raise (part of an accepted write that
   * failed, e.g. held comments a publish could not post): `again` is its
   * Retry. Failures with its `dedup` are retired first.
   */
  failed(w: { kind: WriteKind; dedup: Dedup; label: string; subject?: K | readonly K[] }, error: WriteError, again: () => Promise<boolean>): void {
    this.retire((f) => f.dedup === w.dedup);
    this.fail({ id: ++this.seq, kind: w.kind, dedup: w.dedup, label: w.label, subjects: subjectList(w.subject), draft: null }, error, null, again);
  }

  private fail(write: PendingWrite<K>, error: WriteError, text: string | null, again: () => Promise<boolean>) {
    const failure: FailedWrite<K> = {
      ...write,
      error,
      text,
      retry: () => {
        if (!this.failures.includes(failure)) return Promise.resolve(false);
        this.retire((f) => f === failure, () => true);
        return again();
      },
      dismiss: () => this.retire((f) => f === failure),
    };
    this.setFailures([...this.failures, failure]);
  }

  /** Take failures off the list; each gives its text back to its box unless `keepsText` (it is being sent again). */
  private retire(which: (f: FailedWrite<K>) => boolean, keepsText: (f: FailedWrite<K>) => boolean = () => false) {
    const gone = this.failures.filter(which);
    if (!gone.length) return;
    this.setFailures(this.failures.filter((f) => !which(f)));
    for (const f of gone) if (f.draft && f.text && !keepsText(f)) this.giveBack(f.draft, f.text);
  }

  private giveBack(key: string, text: string) {
    void returnDraft(key, text);
    for (const take of this.listeners.get(key) ?? []) take(text);
  }
}
